import {
  asapScheduler,
  catchError,
  defer,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  subscribeOn,
  switchMap,
  throwError,
} from 'rxjs';
import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { NotificationService } from './notification.service';
import { SOL_PREFERENCE_STORE, SolPreferenceStore } from './sol-preference-store';
import { SolPreferenceWriteOptions } from '../models/core/sol-preference';
import { SolSettingKey } from '../models/core/sol-setting-key';
import { PreferenceTools } from '../tools/preference-tools';

/**
 * Local cache in front of the preference store; the store stays authoritative once it lands.
 *
 * Every entry point takes the `SolSettingKey` the value belongs to, so a caller reads back the type
 * it wrote and a stored shape that no longer fits reads as absent.
 */
@Injectable({
  providedIn: 'root',
})
export class PreferenceService {
  private static readonly STORAGE_PREFIX = 'preference.';

  // Dependencies
  private readonly injector = inject(Injector);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);

  // Cache
  private store?: SolPreferenceStore;
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));
  }

  public cached<V>(key: SolSettingKey<V>): V | undefined {
    return key.toStored(this.localStorageService.get(PreferenceService.toStorageKey(key)));
  }

  public cache<V>(key: SolSettingKey<V>, value: V): void {
    this.localStorageService.set(PreferenceService.toStorageKey(key), value);
  }

  public save<V>(key: SolSettingKey<V>, value: V, options?: SolPreferenceWriteOptions): Observable<void> {
    const previous = this.cached(key);
    this.cache(key, value);
    const saved = this.whenStored().pipe(
      switchMap(store => store.save(key.name, value)),
      catchError((error: unknown) => {
        this.restore(key, previous);
        if (options?.announcedAsToast === true) {
          this.announce(error);
        }
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    saved.subscribe({
      error: (error: unknown) => console.error(`Could not save the setting '${key.name}'`, error),
    });
    return saved;
  }

  private announce(error: unknown): void {
    this.notificationService.show(PreferenceTools.toFailureType(error), PreferenceTools.toFailureKey(error));
  }

  private restore<V>(key: SolSettingKey<V>, previous: V | undefined): void {
    if (previous === undefined) {
      this.localStorageService.remove(PreferenceService.toStorageKey(key));
      return;
    }
    this.cache(key, previous);
  }

  public reset<V>(key: SolSettingKey<V>, options?: SolPreferenceWriteOptions): Observable<void> {
    const previous = this.cached(key);
    this.localStorageService.remove(PreferenceService.toStorageKey(key));
    const cleared = this.whenStored().pipe(
      switchMap(store => store.reset(key.name)),
      catchError((error: unknown) => {
        this.restore(key, previous);
        if (options?.announcedAsToast === true) {
          this.announce(error);
        }
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    cleared.subscribe({
      error: (error: unknown) => console.error(`Could not reset the setting '${key.name}'`, error),
    });
    return cleared;
  }

  public load<V>(key: SolSettingKey<V>): Observable<V | undefined> {
    return this.whenStored().pipe(
      switchMap(store => store.load(key.name)),
      map(value => key.toStored(value)),
    );
  }

  public prePainted(): Observable<Record<string, unknown>> {
    return this.whenStored().pipe(switchMap(store => store.prePaint()));
  }

  private whenStored(): Observable<SolPreferenceStore> {
    return defer(() => of(this.toStore())).pipe(
      subscribeOn(asapScheduler),
      filter((store): store is SolPreferenceStore => store !== undefined),
    );
  }

  private toStore(): SolPreferenceStore | undefined {
    return this.destroyed ? undefined : (this.store ??= this.injector.get(SOL_PREFERENCE_STORE));
  }

  private static toStorageKey<V>(key: SolSettingKey<V>): string {
    return PreferenceService.STORAGE_PREFIX + key.name;
  }
}
